import { useId, useState } from 'react';
import { format } from 'date-fns-tz';

import { initials, PersonAvatar } from 'components/person-avatar';
import { TaskItemProps, useTaskItemEcoa } from 'components/task-item-ecoa/useTaskItemEcoa';
import Button from 'components/ui/button';
import { ModalFrame } from 'components/ui/modal-frame';
import { HUMANOGRAMA, PilulaDaEtapa } from 'pages/RealizacaoDestaque/components/AgendaDaRealizacao';
import { rostoDoAutor } from 'pages/RealizacaoDestaque/identidade-do-participante';
import { FOCO_VISIVEL } from 'utils/foco';

import { eixoDaAcao, FaseEcoa } from '../acoes-do-semestre';
import { etapasDaAcaoEcoa, ModeloDaAcaoEcoa } from '../andamento-da-acao';
import { reguaDaFilaEcoa } from '../etapas-ecoa';
import { dataCurta, ModaisDaAcaoEcoa } from './comum-dos-cartoes';
import DetalhesDaAcaoEcoa from './DetalhesDaAcaoEcoa';
import ExclusaoDoIntegrante from './ExclusaoDoIntegrante';

export interface CartaoDaFilaEcoaProps extends TaskItemProps {
  /** Opção "semestral" da fila: o andamento marca a Bonificação. */
  semestral: boolean;
  /** Horário do servidor da página. */
  agora: number;
}


function dataLonga(ms: number | undefined): string {
  return typeof ms === 'number' ? format(new Date(ms), 'dd/MM/yyyy') : '';
}

const BOTAO = `inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-jj-sm border px-5 py-[11px] text-[11px] font-bold uppercase tracking-[0.04em] ${FOCO_VISIVEL}`;
// SPEC_DEVIATION: a ação principal usa `brand-blue-dark` no lugar do `brand-blue` do design, a mesma regra da fila da
// Realização Destaque. Reason: ECN-61, o amarelo de 11px fica em 4,44:1 no azul e em 8,13:1 no azul-escuro (medido por
// src/test-utils/contraste.ts; prova em __tests__/cartao-da-fila-ecoa.spec.tsx, "contraste (ECN-61)").
const PRINCIPAL = 'border-brand-blue-dark bg-brand-blue-dark text-brand-yellow';
const SECUNDARIO = 'border-brand-blue-dark bg-brand-white text-brand-blue-dark';
const DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-blue-dark hover:underline ${FOCO_VISIVEL}`;
const CONTORNADO = `inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-jj-sm border border-surface-300 bg-brand-white px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-blue ${FOCO_VISIVEL}`;

interface AcaoDoCartao {
  rotulo: string;
  principal?: boolean;
  executar: () => void;
}

/**
 * Ações da barra do cartão nas MESMAS condições do JSX do `TaskItemEcoa` (caixa de apreciação, cabeçalho e feedback).
 * Promover, Selo e Pit Stop ficam de fora: a fila ECOA não passa `promote` e a ação ECOA não tem `prosseguir_*`.
 */
function acoesDaCaixa(modelo: ModeloDaAcaoEcoa, onDouble: TaskItemProps['onDouble']): AcaoDoCartao[] {
  const { data, status, showModal } = modelo;
  if (!(modelo.canViewBoxActions() || modelo.pitStopCanEvaluate) || modelo.isView) return [];

  const acoes: AcaoDoCartao[] = [];
  const aguardandoSemEtapa = !data.stageNumber && (!status || status === 'aguardando');

  if (aguardandoSemEtapa && modelo.canValidate) {
    acoes.push({ rotulo: 'Validar', principal: true, executar: () => showModal(`validation-accept-${data._id}`) });
  }
  if (aguardandoSemEtapa) {
    acoes.push({ rotulo: 'Dar como ciente', executar: () => showModal(`validation-known-${data._id}`) });
  }
  if (modelo.double) {
    acoes.push({
      rotulo: 'Bonificar',
      principal: true,
      executar: () =>
        modelo.doubleByPassCod5 && typeof onDouble === 'function' ? onDouble(data) : showModal(`validation-double-${data._id}`),
    });
  }
  if (modelo.double || modelo.canFinalize) {
    acoes.push({ rotulo: 'Finalizar', executar: () => showModal(`validation-stop-${data._id}`, { type: 'double' }) });
  }

  return acoes;
}

function IconeDeMensagem() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/**
 * Cartão da fila do tutor e do coordenador (ECN-46, ECN-48, ECN-54). Recebe as props que a `ApreciarAcao` passa ao
 * `TaskItemEcoa` e usa o mesmo hook e os mesmos `ModalActions`; só o desenho é novo.
 */
export default function CartaoDaFilaEcoa({ semestral, agora, ...props }: CartaoDaFilaEcoaProps) {
  const modelo = useTaskItemEcoa(props);
  const { data, playerInfo } = modelo;
  const tituloId = useId();
  const detalhesId = useId();
  const [lendoFeedback, setLendoFeedback] = useState(false);

  const etapaAtual = etapasDaAcaoEcoa(modelo).find(etapa => etapa.atual);
  const andamento = reguaDaFilaEcoa(props.cycle as FaseEcoa | undefined, agora, semestral);
  const acoes = acoesDaCaixa(modelo, props.onDouble);
  const meuFeedback = Array.isArray(data.feedback)
    ? data.feedback.find((feedback: any) => feedback.evaluatorId === playerInfo?.chave)
    : undefined;
  const integrantes = Array.isArray(data.team) ? data.team.length : 0;
  const nomeDoAutor = data.owner_name_guerra || data.owner_name || '';
  const responsavel = data.original_manager_detail?.nome_guerra ? modelo.capitalizeWords(data.original_manager_detail.nome_guerra) : undefined;

  return (
    <article
      aria-labelledby={tituloId}
      className="overflow-hidden rounded-[14px] border border-surface-300 bg-brand-white shadow-[0_1px_3px_rgba(17,24,39,.05)]"
    >
      <div className="px-5 pb-[18px] pt-5">
        <div className="flex flex-wrap items-start gap-3.5">
          <PersonAvatar
            {...rostoDoAutor(data)}
            initials={initials(nomeDoAutor)}
            size={42}
            initialsClassName="bg-brand-purple-light font-display text-[13px] font-bold text-brand-navy"
          />
          <div className="min-w-0 flex-[1_1_300px]">
            <h3 id={tituloId} className="m-0 font-display text-[17px] font-bold leading-[1.3] text-brand-blue-dark">
              {data.title || '---'}
            </h3>
            <p className="m-0 mt-[7px] text-[11.5px] leading-[1.5] text-content-soft">
              <a
                href={`${HUMANOGRAMA}${data.owner}`}
                target="_blank"
                rel="noreferrer"
                className={`font-bold text-link hover:text-link-hover ${FOCO_VISIVEL}`}
              >
                {`${nomeDoAutor.split(' ')[0]} (${data.owner})`}
              </a>
              {` · cadastrou em ${dataCurta(data.created)}`}
              {!!data.updated && ` · atualizada em ${dataCurta(data.updated)}`}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 md:flex-col md:items-end">
            <PilulaDaEtapa status={etapaAtual?.status} />
            <span
              className={`whitespace-nowrap rounded-jj-pill border px-[11px] py-1 text-[9.5px] font-bold uppercase tracking-[0.04em] ${
                modelo.hasFeedback ? 'border-brand-green text-brand-navy' : 'border-surface-300 text-content'
              }`}
            >
              {modelo.hasFeedback ? 'Feedback concedido' : 'Sem feedback'}
            </span>
            {modelo.showEditButton && (
              <button type="button" onClick={modelo.handleEdit} className={DE_TEXTO}>
                Editar ação
              </button>
            )}
          </div>
        </div>

        <dl className="m-0 mt-4 grid grid-cols-2 gap-3.5 border-t border-surface-200 pt-3.5 md:grid-cols-4">
          <div className="min-w-0">
            <dt className="mb-[5px] text-[10px] text-content-soft">Eixo temático</dt>
            <dd className="m-0 text-[11.5px] leading-[1.45] text-content">{eixoDaAcao(data)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="mb-[5px] text-[10px] text-content-soft">Período da ação</dt>
            <dd className="m-0 text-[11.5px] leading-[1.45] text-content">
              {data.start && data.end ? `${modelo.getFormattedDate(data.start)} a ${modelo.getFormattedDate(data.end)}` : '---'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="mb-[5px] text-[10px] text-content-soft">Integrantes</dt>
            <dd className="m-0 text-[11.5px] leading-[1.45] text-content">{`${integrantes} integrantes`}</dd>
          </div>
          <div className="min-w-0">
            <dt className="mb-[5px] text-[10px] text-content-soft">Pontos concedidos</dt>
            <dd className="m-0 font-display text-[15px] font-bold text-brand-blue">{modelo.points}</dd>
          </div>
        </dl>

        <section aria-labelledby={`${tituloId}-andamento`} className="mt-3.5 rounded-jj-md border border-surface-200 px-4 py-3.5">
          <h4 id={`${tituloId}-andamento`} className="m-0 mb-3 text-[9.5px] font-bold uppercase tracking-[0.06em] text-content-soft">
            Andamento da ação
          </h4>
          <ol aria-label="Andamento da ação" className="m-0 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-3">
            {andamento.map(etapa => (
              <li
                key={etapa.titulo}
                aria-current={etapa.atual ? 'step' : undefined}
                className={`flex min-w-0 flex-col gap-[3px] rounded-jj-md px-[11px] py-2 ${
                  etapa.atual ? 'bg-brand-blue-dark text-brand-white' : 'bg-surface-100 text-content'
                }`}
              >
                <b className="font-display text-xs leading-[1.2]">{etapa.titulo}</b>
                <span className="text-[10.5px]">{etapa.periodo ?? 'Sem datas nesta fase'}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* SPEC_DEVIATION: o prazo do `evaluationData` vem do `Countdown` com o vermelho antigo; aqui ele passa ao
            `text-urgent` da nova identidade sem tocar no hook (mesma regra do cartão da fila da Realização Destaque). */}
        <div className="mt-3 text-[11.5px] text-content-soft empty:hidden [&_.msg-countdown_strong_*]:!text-urgent">
          {modelo.evaluationData}
        </div>

        <div id={detalhesId}>
          {modelo.oppened && (
            <>
              <DetalhesDaAcaoEcoa
                data={data}
                getFormattedDate={modelo.getFormattedDate}
                responsavel={responsavel}
                acoesDoIntegrante={pessoa => <ExclusaoDoIntegrante pessoa={pessoa} modelo={modelo} />}
              />
              {meuFeedback && (
                <section aria-label="Seu feedback" className="mt-3 rounded-jj-md bg-surface-100 px-5 py-3.5">
                  <h4 className="m-0 mb-1 text-[11px] font-normal text-content">{`Seu feedback · ${dataLonga(meuFeedback.created)}`}</h4>
                  {/* Mesmo HTML que o `TaskItemEcoa` renderiza para a mensagem do feedback. */}
                  <p className="m-0 text-xs leading-[1.6] text-content" dangerouslySetInnerHTML={{ __html: meuFeedback.message }} />
                </section>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-surface-200 bg-surface-100 px-5 py-3.5">
        {acoes.map(acao => (
          <button key={acao.rotulo} type="button" onClick={acao.executar} className={`${BOTAO} ${acao.principal ? PRINCIPAL : SECUNDARIO}`}>
            {acao.rotulo}
          </button>
        ))}
        {modelo.showResetButton && (
          <button type="button" onClick={() => modelo.showModal(`validation-reset-${data._id}`)} className={DE_TEXTO}>
            Desfazer
          </button>
        )}
        {modelo.canSendFeedback() && (
          <button
            type="button"
            title="Conte à equipe o que achou dessa entrega"
            onClick={() => modelo.showModal(`validation-feedback-${data._id}`)}
            className={CONTORNADO}
          >
            <IconeDeMensagem />
            Enviar feedback
          </button>
        )}
        {modelo.hasFeedback && meuFeedback && (
          <button type="button" aria-haspopup="dialog" onClick={() => setLendoFeedback(true)} className={CONTORNADO}>
            <IconeDeMensagem />
            Ver feedback
          </button>
        )}
        <button
          type="button"
          aria-expanded={modelo.oppened}
          aria-controls={detalhesId}
          onClick={() => modelo.setOppened(!modelo.oppened)}
          className={`ml-auto inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent px-3.5 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.04em] text-content ${FOCO_VISIVEL}`}
        >
          <span aria-hidden="true" className="text-[13px] leading-none">
            {modelo.oppened ? '−' : '+'}
          </span>
          {`Ver ${modelo.oppened ? 'menos' : 'mais'} informações`}
        </button>
      </div>

      {lendoFeedback && meuFeedback && (
        <ModalFrame
          isOpen
          appearance="cartao"
          size="sm"
          title="Feedback enviado"
          subtitle={data.title}
          onClose={() => setLendoFeedback(false)}
          closeLabel="Fechar modal"
          footer={
            <Button variant="secundario" className="ml-auto" onClick={() => setLendoFeedback(false)}>
              Fechar
            </Button>
          }
        >
          <div className="flex flex-col gap-2 px-5 pt-2 lg:px-[30px]">
            <p className="m-0 text-[11px] text-content-soft">{`Enviado em ${dataLonga(meuFeedback.created)}`}</p>
            <p className="m-0 text-[12.5px] leading-[1.65] text-content" dangerouslySetInnerHTML={{ __html: meuFeedback.message }} />
          </div>
        </ModalFrame>
      )}

      <ModaisDaAcaoEcoa modelo={modelo} />
    </article>
  );
}
